import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * Barra horizontal de etapas — sticky, distribui na largura, destaca etapa visível.
 */
export default function AgentFormStepNav({ sections = [], onSelectSection }) {
  const { t } = useTranslation("settings");
  const [activeId, setActiveId] = useState(sections[0]?.id ?? null);

  useEffect(() => {
    if (!sections.length) return undefined;

    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visible.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        let bestId = null;
        let bestRatio = 0;
        sections.forEach((sec) => {
          const ratio = visible.get(`agent-form-section-${sec.id}`) ?? 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = sec.id;
          }
        });
        if (bestId) setActiveId(bestId);
      },
      { rootMargin: "-20% 0px -55% 0px", threshold: [0, 0.15, 0.4, 0.7, 1] }
    );

    sections.forEach((sec) => {
      const el = document.getElementById(`agent-form-section-${sec.id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  if (!sections.length) return null;

  return (
    <Box
      sx={{
        position: "sticky",
        top: { xs: 56, md: 64 },
        zIndex: 2,
        mx: { xs: -2, md: -3 },
        px: { xs: 1.5, md: 2 },
        py: 1,
        mb: 0.5,
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "grey.50",
        borderTop: 1,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: `repeat(${sections.length}, minmax(128px, 1fr))`,
            sm: `repeat(${sections.length}, minmax(0, 1fr))`,
          },
          gap: 0.75,
          overflowX: { xs: "auto", sm: "visible" },
          WebkitOverflowScrolling: "touch",
          pb: { xs: 0.25, sm: 0 },
        }}
      >
        {sections.map((sec) => {
          const isActive = activeId === sec.id;
          return (
            <Box
              key={sec.id}
              component="button"
              type="button"
              onClick={() => onSelectSection(sec.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: { xs: 1, sm: 1.25 },
                py: 0.875,
                border: 1,
                borderColor: isActive ? "primary.main" : "transparent",
                borderRadius: 1.5,
                bgcolor: isActive ? "background.paper" : "transparent",
                boxShadow: isActive ? 1 : 0,
                cursor: "pointer",
                textAlign: "left",
                minWidth: { xs: 128, sm: 0 },
                transition: "background-color 0.15s, border-color 0.15s, box-shadow 0.15s",
                "&:hover": {
                  bgcolor: "background.paper",
                  borderColor: isActive ? "primary.main" : "divider",
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  flexShrink: 0,
                  fontSize: 11,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isActive ? "primary.main" : "action.selected",
                  color: isActive ? "primary.contrastText" : "text.secondary",
                  border: 1,
                  borderColor: isActive ? "primary.main" : "divider",
                }}
              >
                {sec.step}
              </Box>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "text.primary" : "text.secondary",
                  lineHeight: 1.25,
                  fontSize: { xs: 12, sm: 13 },
                }}
              >
                {t(sec.labelKey)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
